import { useLayoutEffect, useState, type RefObject } from 'react'
import { motion } from 'framer-motion'
import { useEliteBuyCtaExtras } from './EliteBuyCtaExtrasContext'
import {
  ELITE_ABOUT_FAB_GAP_PX,
  ELITE_ABOUT_FAB_PORTRAIT_PX,
  readEliteAboutFabSafeBottomPx
} from './eliteAboutFabLayout'
import { openEliteAboutVideo, VIDEO_TRUST_MARKER_PORTRAIT } from './VideoTrustMarker'

const WATCH_ACCENT = '#CD5C2E'
/** Stop retrying CTA attach after ~1s of animation frames. */
const MAX_CTA_ATTACH_FRAMES = 60
/** Fallback when live measure fails — tall enough to clear consent sticky CTAs. */
const FALLBACK_ELITE_CTA_HEIGHT_PX = 220
/** Give layout/fonts a short window before falling back. */
const MEASURE_FALLBACK_TIMEOUT_MS = 400

function readElementHeightPx(el: HTMLElement | null | undefined): number {
  if (!el) return 0
  try {
    const rectHeight = el.getBoundingClientRect()?.height ?? 0
    if (rectHeight > 0) return Math.ceil(rectHeight)
    return el.offsetHeight ?? 0
  } catch {
    return 0
  }
}

function readResizeObserverHeightPx(entry: ResizeObserverEntry | undefined, el: HTMLElement): number {
  try {
    const box = entry?.borderBoxSize
    if (box) {
      const size = Array.isArray(box) ? box[0] : box
      if (size?.blockSize > 0) return Math.ceil(size.blockSize)
    }
  } catch {
    // fall through to element measure
  }
  return readElementHeightPx(el)
}

type EliteAboutVideoFabProps = {
  /** When false, FAB stays mounted but hidden. Defaults false until the clear-of-band hook says show. */
  showFab?: boolean
  isEliteCtaVisible?: boolean
  /** Floating Elite CTA element — its measured height lifts the FAB. */
  eliteCtaRef?: RefObject<HTMLElement>
  /**
   * Bottom offset of the Elite CTA container (distance from viewport bottom).
   * Defaults to safe-area bottom padding.
   */
  eliteCtaBottomPx?: number
}

export function EliteAboutVideoFab({
  showFab = false,
  isEliteCtaVisible = false,
  eliteCtaRef,
  eliteCtaBottomPx
}: EliteAboutVideoFabProps) {
  const buyCtaExtras = useEliteBuyCtaExtras()
  const [eliteCtaHeightPx, setEliteCtaHeightPx] = useState(0)
  const safeBottomPx = readEliteAboutFabSafeBottomPx()
  const ctaBottomPx = Number.isFinite(eliteCtaBottomPx) ? (eliteCtaBottomPx as number) : safeBottomPx
  const bottomPx =
    isEliteCtaVisible && eliteCtaHeightPx > 0
      ? ctaBottomPx + eliteCtaHeightPx + ELITE_ABOUT_FAB_GAP_PX
      : safeBottomPx + ELITE_ABOUT_FAB_GAP_PX

  useLayoutEffect(() => {
    if (!showFab || !isEliteCtaVisible) {
      setEliteCtaHeightPx(0)
      return
    }

    let cancelled = false
    let measuredSuccessfully = false
    let resizeObserver: ResizeObserver | null = null
    let rafId = 0
    let measureRafId = 0
    let framesTried = 0

    const applyHeight = (nextHeightPx: number, isFallback = false) => {
      if (cancelled || !Number.isFinite(nextHeightPx) || nextHeightPx <= 0) return
      if (!isFallback) measuredSuccessfully = true
      if (isFallback && measuredSuccessfully) return
      setEliteCtaHeightPx((prev) => (prev === nextHeightPx ? prev : nextHeightPx))
    }

    const applyFallback = () => {
      applyHeight(FALLBACK_ELITE_CTA_HEIGHT_PX, true)
    }

    const fallbackTimeoutId = window.setTimeout(() => {
      if (!cancelled && !measuredSuccessfully) applyFallback()
    }, MEASURE_FALLBACK_TIMEOUT_MS)

    const observe = (el: HTMLElement) => {
      if (!el?.isConnected) {
        applyFallback()
        return
      }

      applyHeight(readElementHeightPx(el))

      measureRafId = requestAnimationFrame(() => {
        if (cancelled) return
        applyHeight(readElementHeightPx(el))
      })

      if (typeof ResizeObserver === 'undefined') {
        if (!measuredSuccessfully) applyFallback()
        return
      }

      try {
        resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0]
          if (!entry) return
          applyHeight(readResizeObserverHeightPx(entry, el))
        })
        try {
          resizeObserver.observe(el, { box: 'border-box' })
        } catch {
          resizeObserver.observe(el)
        }
      } catch {
        if (!measuredSuccessfully) applyFallback()
      }
    }

    const attach = () => {
      if (cancelled) return
      const el = eliteCtaRef?.current ?? null
      if (!el) {
        framesTried += 1
        if (framesTried >= MAX_CTA_ATTACH_FRAMES) {
          applyFallback()
          return
        }
        rafId = requestAnimationFrame(attach)
        return
      }
      observe(el)
    }

    attach()

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      cancelAnimationFrame(measureRafId)
      window.clearTimeout(fallbackTimeoutId)
      try {
        resizeObserver?.disconnect()
      } catch {
        // ignore
      }
    }
  }, [showFab, isEliteCtaVisible, eliteCtaRef])

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, bottom: bottomPx }}
      animate={{
        opacity: showFab ? 1 : 0,
        y: showFab ? 0 : 12,
        bottom: bottomPx
      }}
      transition={{
        // Hide instantly so a mistaken show never fades out as a visible flash.
        opacity: { type: 'tween', duration: showFab ? 0.35 : 0, ease: 'easeOut' },
        y: { type: 'tween', duration: showFab ? 0.35 : 0, ease: 'easeOut' },
        bottom: { type: 'tween', duration: 0.3, ease: 'easeOut' }
      }}
      aria-hidden={!showFab}
      className={`fixed right-5 z-[60] ${showFab ? 'pointer-events-auto' : 'pointer-events-none'}`}
    >
      <div
        onClick={() => {
          if (!showFab) return
          try {
            openEliteAboutVideo('fab', buyCtaExtras)
          } catch {
            // ignore — never crash the landing page from FAB tap
          }
        }}
        className='inline-flex cursor-pointer items-center gap-2.5 rounded-full border border-[#E8D9C8] bg-white py-1.5 pl-1.5 pr-4 shadow-[0_4px_16px_rgba(0,0,0,0.08)]'
      >
        <div
          className='relative shrink-0 overflow-hidden rounded-full'
          style={{ width: ELITE_ABOUT_FAB_PORTRAIT_PX, height: ELITE_ABOUT_FAB_PORTRAIT_PX }}
        >
          <img
            alt=''
            src={VIDEO_TRUST_MARKER_PORTRAIT}
            className='h-full w-full object-cover object-[center_20%]'
          />
          <div className='pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10'>
            <PlayTriangle />
          </div>
        </div>
        <div className='flex flex-col gap-1 pr-0.5'>
          <div className='flex items-center gap-1'>
            <SoundWaveIcon />
            <span
              className='text-[10px] font-[500] uppercase leading-none tracking-[0.06em]'
              style={{ color: WATCH_ACCENT }}
            >
              Watch
            </span>
          </div>
          <span className='text-[15px] font-[500] leading-tight text-[#232323]'>About Elite</span>
        </div>
      </div>
    </motion.div>
  )
}

export function PlayTriangle() {
  return (
    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden>
      <path d='M4.5 2.8v8.4L11.2 7 4.5 2.8Z' fill='white' />
    </svg>
  )
}

export function SoundWaveIcon() {
  const barTransition = {
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut' as const
  }

  return (
    <div className='flex h-[10px] items-center gap-[2px]' aria-hidden>
      <motion.div
        className='w-[2px] rounded-full'
        style={{ backgroundColor: WATCH_ACCENT }}
        animate={{ height: [3, 8, 3] }}
        transition={barTransition}
      />
      <motion.div
        className='w-[2px] rounded-full'
        style={{ backgroundColor: WATCH_ACCENT }}
        animate={{ height: [8, 3, 8] }}
        transition={barTransition}
      />
      <motion.div
        className='w-[2px] rounded-full'
        style={{ backgroundColor: WATCH_ACCENT }}
        animate={{ height: [3, 8, 3] }}
        transition={barTransition}
      />
    </div>
  )
}
